import { useEffect, useState } from 'react'
import { Bold, ChevronDown, Italic, List, ListChecks, Plus } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import type { Editor } from '@tiptap/core'
import type { LucideIcon } from 'lucide-react'
import { useIsMobile } from '@/hooks/useMobile'
import { cn } from '@/lib/utils'

/**
 * The formatting bar that sits on the keyboard while a doc is being edited
 * on a phone (design 36d). "+" types "/", which opens the block menu (where
 * Task is one option); the last button puts the keyboard away.
 */
export function PhoneFormatBar({ editor }: { editor: Editor | null }) {
  const { t } = useTranslation('common')
  const isPhone = useIsMobile()
  const [, rerender] = useState(0)

  // Focus and marks live on the editor, not in React state.
  useEffect(() => {
    if (!editor) return
    const update = () => rerender((n) => n + 1)
    editor.on('focus', update)
    editor.on('blur', update)
    editor.on('selectionUpdate', update)
    editor.on('transaction', update)
    return () => {
      editor.off('focus', update)
      editor.off('blur', update)
      editor.off('selectionUpdate', update)
      editor.off('transaction', update)
    }
  }, [editor])

  if (!isPhone || !editor || !editor.isEditable || !editor.isFocused) return null

  const actions: Array<{ key: string; icon: LucideIcon; label: string; active?: boolean; run: () => void }> = [
    { key: 'block', icon: Plus, label: t('editor.phoneBar.block'), run: () => editor.chain().focus().insertContent('/').run() },
    { key: 'bold', icon: Bold, label: t('editor.phoneBar.bold'), active: editor.isActive('bold'), run: () => editor.chain().focus().toggleBold().run() },
    { key: 'italic', icon: Italic, label: t('editor.phoneBar.italic'), active: editor.isActive('italic'), run: () => editor.chain().focus().toggleItalic().run() },
    { key: 'bullets', icon: List, label: t('editor.phoneBar.bullets'), active: editor.isActive('bulletList'), run: () => editor.chain().focus().toggleBulletList().run() },
    { key: 'checklist', icon: ListChecks, label: t('editor.phoneBar.checklist'), active: editor.isActive('taskList'), run: () => editor.chain().focus().toggleTaskList().run() },
  ]

  return (
    <div
      role="toolbar"
      aria-label={t('editor.phoneBar.label')}
      className="fixed inset-x-0 bottom-[var(--kb,0px)] z-40 flex h-12 items-center justify-between border-t bg-chrome px-2"
      // Keep the editor focused (and the keyboard up) while tapping.
      onPointerDown={(event) => event.preventDefault()}
    >
      {actions.map(({ key, icon: Icon, label, active, run }) => (
        <button
          key={key}
          type="button"
          aria-label={label}
          aria-pressed={active}
          onClick={run}
          className={cn(
            'grid size-11 place-items-center rounded-lg text-foreground',
            active && 'bg-secondary text-brand',
          )}
        >
          <Icon className="size-5" />
        </button>
      ))}
      <button
        type="button"
        aria-label={t('editor.phoneBar.done')}
        onClick={() => editor.commands.blur()}
        className="grid size-11 place-items-center rounded-lg text-muted-foreground"
      >
        <ChevronDown className="size-5" />
      </button>
    </div>
  )
}
